import {
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { PlayerControls } from './PlayerControls';
import { ReactionOverlay } from './ReactionOverlay';
import { usePlayerStore } from '@/store/playerStore';
import { debounce } from '@/lib/utils';
import { Loader2, Play } from 'lucide-react';
import type { VideoQuality } from '@/types';

interface VideoPlayerProps {
  /** For host: blob URL of local file. For guest: undefined (srcObject set externally) */
  src?: string;
  /** For guest: the remote MediaStream */
  remoteStream?: MediaStream | null;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (time: number) => void;
  /** Exposed so WatchRoom can call videoRef.current.currentTime etc. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onTimeUpdate?: (time: number) => void;
  onDurationChange?: (duration: number) => void;
  onEnded?: () => void;
  onSpeedChange?: (speed: number) => void;
  onQualityChange?: (quality: VideoQuality) => void;
  onResync?: () => void;
  onToggleTelemetry?: () => void;
  onOpenSubtitleSearch?: () => void;
}

export function VideoPlayer({
  src,
  remoteStream,
  onPlay,
  onPause,
  onSeek,
  videoRef,
  onTimeUpdate,
  onDurationChange,
  onEnded,
  onSpeedChange,
  onQualityChange,
  onResync,
  onToggleTelemetry,
  onOpenSubtitleSearch,
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitingDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    volume,
    muted,
    playbackRate,
    isPlaying,
    isBuffering,
    autoplayBlocked,
    activeSubtitle,
    subtitleEnabled,
    setPlaying,
    setCurrentTime,
    setDuration,
    setVolume,
    setMuted,
    setBuffering,
    setAutoplayBlocked,
    setFullscreen,
    setSubtitleEnabled,
    isFullscreen,
  } = usePlayerStore();

  // Attach local blob src or incoming remote stream
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Prioritize native Blob URL (host local file or guest 100% RAM buffer)
    if (src && src.startsWith('blob:')) {
      if (video.src !== src) {
        const preserveTime = video.currentTime || usePlayerStore.getState().currentTime || 0;
        video.srcObject = null;
        video.src = src;
        video.load();
        if (preserveTime > 0) {
          video.currentTime = preserveTime;
        }
        if (usePlayerStore.getState().isPlaying) {
          video.play()
            .then(() => {
              setAutoplayBlocked(false);
              setBuffering(false);
            })
            .catch((err) => {
              console.log('[VideoPlayer] Play interrupted or blocked by browser policy:', err);
              if (err.name === 'NotAllowedError') {
                video.muted = true;
                setMuted(true);
                video.play()
                  .then(() => {
                    setAutoplayBlocked(false);
                    setBuffering(false);
                  })
                  .catch(() => {
                    setAutoplayBlocked(true);
                  });
              }
            });
        }
      }
    } else if (remoteStream) {
      if (video.srcObject !== remoteStream) {
        video.removeAttribute('src');
        video.srcObject = remoteStream;
      }
      if (usePlayerStore.getState().isPlaying) {
        video.play()
          .then(() => {
            setAutoplayBlocked(false);
            setBuffering(false);
          })
          .catch((err) => {
            console.log('[VideoPlayer] Remote stream play blocked by browser policy:', err);
            if (err.name === 'NotAllowedError') {
              video.muted = true;
              setMuted(true);
              video.play()
                .then(() => {
                  setAutoplayBlocked(false);
                  setBuffering(false);
                })
                .catch(() => {
                  setAutoplayBlocked(true);
                });
            } else {
              setAutoplayBlocked(true);
            }
          });
      } else {
        if (!video.paused) {
          video.pause();
        }
      }
    } else if (src) {
      if (video.src !== src) {
        video.srcObject = null;
        video.src = src;
        video.load();
        if (usePlayerStore.getState().isPlaying) {
          video.play().catch(() => {});
        }
      }
    }
  }, [src, remoteStream, videoRef, setBuffering, setMuted, setAutoplayBlocked]);

  // Sync volume, mute state, and playback rate
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    video.muted = muted;
    video.playbackRate = playbackRate;
  }, [volume, muted, playbackRate, videoRef]);

  // Auto-hide controls after 3 seconds of inactivity
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = setTimeout(() => {
      setShowControls(false);
    }, 3200);
  }, []);

  useEffect(() => {
    resetHideTimer();
    return () => {
      if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current);
    };
  }, [resetHideTimer]);

  // Fullscreen management
  const handleFullscreen = useCallback(async () => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      try {
        await container.requestFullscreen();
        setFullscreen(true);
      } catch (err) {
        console.warn('Fullscreen request failed:', err);
      }
    } else {
      try {
        await document.exitFullscreen();
        setFullscreen(false);
      } catch (err) {
        console.warn('Exit fullscreen failed:', err);
      }
    }
  }, [setFullscreen]);

  useEffect(() => {
    const onFsChange = () => {
      setFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [setFullscreen]);

  // Picture in Picture
  const handlePiP = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (video.requestPictureInPicture) {
        await video.requestPictureInPicture();
      }
    } catch (err) {
      console.warn('PiP error:', err);
    }
  }, [videoRef]);

  // Manual playback trigger for autoplay unlock on mobile / secondary devices
  const handleManualPlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    try {
      await video.play();
      setAutoplayBlocked(false);
      setBuffering(false);
      onPlay();
    } catch {
      // If audio is blocked, try muted playback then prompt unmute
      video.muted = true;
      setMuted(true);
      try {
        await video.play();
        setAutoplayBlocked(false);
        setBuffering(false);
        onPlay();
      } catch { }
    }
  }, [videoRef, onPlay, setBuffering, setMuted]);

  // Smooth local scrubber progress ticker for guest remote stream while playing
  useEffect(() => {
    if (!remoteStream || !usePlayerStore.getState().isPlaying) return;
    const interval = setInterval(() => {
      const cur = usePlayerStore.getState().currentTime;
      const dur = usePlayerStore.getState().duration;
      if (dur > 0 && cur < dur) {
        const next = cur + 1;
        setCurrentTime(next);
        onTimeUpdate?.(next);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [remoteStream, setCurrentTime, onTimeUpdate]);

  // Skip helper (works collaboratively for both host and guest)
  const handleSkip = useCallback(
    (seconds: number) => {
      const video = videoRef.current;
      const duration = usePlayerStore.getState().duration || (video && !video.srcObject ? video.duration : 0);
      const current = usePlayerStore.getState().currentTime || 0;
      const target = Math.max(0, Math.min(duration || 0, current + seconds));
      if (video && !video.srcObject) {
        video.currentTime = target;
      }
      setCurrentTime(target);
      onSeek(target);
    },
    [onSeek, videoRef, setCurrentTime]
  );

  // Keyboard Shortcuts (Space, F, M, Arrows)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;

      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          resetHideTimer();
          if (video.paused) {
            video.play().catch(() => { });
            onPlay();
          } else {
            video.pause();
            onPause();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          resetHideTimer();
          handleSkip(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          resetHideTimer();
          handleSkip(10);
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          handleFullscreen();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          resetHideTimer();
          setMuted(!muted);
          break;
        case 's':
        case 'S':
          e.preventDefault();
          resetHideTimer();
          setSubtitleEnabled(!subtitleEnabled);
          break;
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onPlay, onPause, handleSkip, handleFullscreen, setMuted, muted, resetHideTimer, videoRef, setSubtitleEnabled, subtitleEnabled]);

  // Debounced seek emitter
  const debouncedSeek = useCallback(
    debounce((time: number) => onSeek(time), 200),
    [onSeek]
  );

  return (
    <div
      ref={containerRef}
      className="video-player-container always-white dark w-full h-full bg-black relative select-none"
      onMouseMove={resetHideTimer}
      onClick={resetHideTimer}
      onDoubleClick={handleFullscreen}
      style={{ cursor: showControls ? 'default' : 'none' }}
    >
      {/* Video Element */}
      <video
        ref={videoRef as React.RefObject<HTMLVideoElement>}
        className="w-full h-full object-contain"
        playsInline
        autoPlay
        onPlay={() => {
          setPlaying(true);
          setBuffering(false);
          setAutoplayBlocked(false);
        }}
        onPause={() => {
          setPlaying(false);
          setBuffering(false);
          if (waitingDebounceTimer.current) clearTimeout(waitingDebounceTimer.current);
        }}
        onTimeUpdate={(e) => {
          const video = e.target as HTMLVideoElement;
          if (!video.srcObject) {
            const t = video.currentTime;
            setCurrentTime(t);
            onTimeUpdate?.(t);
          } else {
            // Guest mode: call onTimeUpdate with store's synchronized movie time for subtitles
            onTimeUpdate?.(usePlayerStore.getState().currentTime);
          }
          if (isBuffering) {
            setBuffering(false);
          }
        }}
        onDurationChange={(e) => {
          const video = e.target as HTMLVideoElement;
          if (!video.srcObject) {
            const d = video.duration;
            if (d && !isNaN(d) && isFinite(d) && d > 0) {
              setDuration(d);
              onDurationChange?.(d);
            }
          }
        }}
        onWaiting={() => {
          if (!autoplayBlocked && usePlayerStore.getState().isPlaying) {
            if (waitingDebounceTimer.current) clearTimeout(waitingDebounceTimer.current);
            waitingDebounceTimer.current = setTimeout(() => {
              if (usePlayerStore.getState().isPlaying) {
                setBuffering(true);
              }
            }, 800);
            // Safety release: never trap user in an indefinite buffer overlay
            setTimeout(() => {
              setBuffering(false);
            }, 3500);
          }
        }}
        onError={(e) => {
          const video = e.target as HTMLVideoElement;
          console.warn('[VideoPlayer] Video element error code:', video.error?.code, video.error?.message);
          setBuffering(false);
          // If a file src failed, clear it in the store so it doesn't get re-applied in an infinite loop
          usePlayerStore.getState().setGuestStreamSrc(null);
          if (remoteStream) {
            console.log('[VideoPlayer] Video error encountered; seamlessly falling back to remoteStream');
            video.removeAttribute('src');
            video.load();
            video.srcObject = remoteStream;
            if (usePlayerStore.getState().isPlaying) {
              video.play().catch(() => {});
            }
          }
        }}
        onPlaying={() => {
          if (waitingDebounceTimer.current) clearTimeout(waitingDebounceTimer.current);
          setBuffering(false);
          setAutoplayBlocked(false);
        }}
        onCanPlay={() => {
          if (waitingDebounceTimer.current) clearTimeout(waitingDebounceTimer.current);
          setBuffering(false);
        }}
        onLoadedData={() => {
          if (waitingDebounceTimer.current) clearTimeout(waitingDebounceTimer.current);
          setBuffering(false);
        }}
        onEnded={onEnded}
      />

      {/* Mobile / Secondary Device Autoplay Prompt Overlay */}
      {autoplayBlocked && (
        <div
          onClick={handleManualPlay}
          className="absolute inset-0 z-30 flex items-center justify-center bg-black/75 backdrop-blur-sm cursor-pointer select-none"
        >
          <div className="bg-cinema-900 border border-lamp/40 p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-3 max-w-sm text-center transform transition-transform hover:scale-105">
            <div className="w-14 h-14 rounded-full bg-lamp/20 border border-lamp/40 flex items-center justify-center text-lamp animate-pulse">
              <Play className="w-7 h-7 fill-current ml-0.5" />
            </div>
            <h3 className="text-base font-bold text-white">Tap to Start Watching</h3>
            <p className="text-xs text-cinema-400 leading-relaxed">
              Your browser paused playback until you interact with the screen. Click anywhere to join the stream with sound.
            </p>
            <button className="mt-2 px-6 py-2 bg-lamp hover:bg-lamp-bright text-cinema-950 font-bold rounded-lg text-xs uppercase tracking-wider shadow">
              Join Stream
            </button>
          </div>
        </div>
      )}

      {/* Floating Emoji Reactions Layer */}
      <ReactionOverlay />

      {/* Buffering Indicator */}
      {isBuffering && isPlaying && !autoplayBlocked && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div className="bg-black/60 border border-cinema-700/60 p-4 rounded-xl flex items-center gap-3 backdrop-blur-sm">
            <Loader2 className="w-5 h-5 text-lamp animate-spin" />
            <span className="text-xs font-mono text-cinema-200 uppercase tracking-wider">Syncing Buffer…</span>
          </div>
        </div>
      )}

      {/* Subtitles Overlay */}
      {subtitleEnabled && activeSubtitle && (
        <div className="absolute bottom-24 left-0 right-0 flex justify-center pointer-events-none px-8 z-20">
          <div className="cinema-subtitle bg-black/75 text-white text-base sm:text-lg font-medium px-4 py-1.5 rounded text-center max-w-[85%] leading-relaxed border border-white/5">
            {activeSubtitle}
          </div>
        </div>
      )}

      {/* Player Controls Bar */}
      <div className={`player-controls-layer ${showControls ? '' : 'controls-hidden'}`}>
        <PlayerControls
          onPlay={() => {
            if (videoRef.current && !videoRef.current.srcObject) {
              videoRef.current?.play().catch(() => { });
            }
            onPlay();
          }}
          onPause={() => {
            if (videoRef.current && !videoRef.current.srcObject) {
              videoRef.current?.pause();
            }
            onPause();
          }}
          onSeek={(t) => {
            if (videoRef.current && !videoRef.current.srcObject) {
              videoRef.current.currentTime = t;
            }
            setCurrentTime(t);
            debouncedSeek(t);
          }}
          onVolumeChange={(v) => setVolume(v)}
          onMuteToggle={() => setMuted(!muted)}
          onFullscreen={handleFullscreen}
          onPiP={handlePiP}
          onSkip={handleSkip}
          onSubtitleToggle={() => setSubtitleEnabled(!subtitleEnabled)}
          onSpeedChange={onSpeedChange}
          onQualityChange={onQualityChange}
          onResync={onResync}
          duration={usePlayerStore.getState().duration}
          onToggleTelemetry={onToggleTelemetry}
          onOpenSubtitleSearch={onOpenSubtitleSearch}
        />
      </div>
    </div>
  );
}
